import { ref, onMounted, type Ref } from 'vue'
import { useSessionRecovery, type RecoverableSession } from '@/composables/useSessionRecovery'
import { getGlobalT } from '@/i18n'
import { getAppMode } from '@/utils/domain'
import { extractApiErrorMessage, logUnexpectedFailure } from '@/utils/errorHandling'

interface UseRecoveryNotificationReturn {
  showRecoveryDialog: Ref<boolean>
  recoverableSessions: Ref<RecoverableSession[]>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  openRecoveryDialog: () => void
  closeRecoveryDialog: () => void
  discardSession: (sessionId: string) => Promise<void>
}

// Singleton state to ensure we only check once per app lifecycle
let hasChecked = false
const globalRecoverableSessions = ref<RecoverableSession[]>([])
const globalError = ref<string | null>(null)
const globalIsLoading = ref(false)

/**
 * Composable for managing the recovery notification at the app level.
 *
 * This composable:
 * 1. Checks for incomplete sessions once on first mount
 * 2. Provides state for showing/hiding the recovery dialog
 * 3. Shares state across multiple component instances (singleton pattern)
 *
 * Usage in App.vue:
 * ```typescript
 * const { showRecoveryDialog, recoverableSessions, openRecoveryDialog, ... } = useRecoveryNotification()
 * ```
 */
export function useRecoveryNotification(): UseRecoveryNotificationReturn {
  const t = getGlobalT()

  const showRecoveryDialog = ref(false)
  const {
    recoverableSessions,
    checkForIncompleteSessions,
    discardSession: doDiscard,
  } = useSessionRecovery()

  async function checkOnMount() {
    // Only the behandelaar app records. `App.vue` mounts this composable for
    // every domain, so without this gate the client and admin portals open the
    // `scrivio-recordings` database — including its version-change migration —
    // on every page load, purely to find the empty result they always find.
    // That third connection is what made a Safari storage eviction on
    // mijn.scrivio.nl look like a recording failure (SCRIVIO-FE-F0).
    if (getAppMode() !== 'behandelaar') return
    if (hasChecked) return
    hasChecked = true

    globalIsLoading.value = true
    try {
      await checkForIncompleteSessions()
      globalRecoverableSessions.value = recoverableSessions.value
    } catch (err) {
      logUnexpectedFailure('Failed to check for incomplete sessions:', err)
      globalError.value = extractApiErrorMessage(err, t('recording.recovery.checkFailed'))
    } finally {
      globalIsLoading.value = false
    }
  }

  function openRecoveryDialog() {
    showRecoveryDialog.value = true
  }

  function closeRecoveryDialog() {
    showRecoveryDialog.value = false
  }

  async function discardSession(sessionId: string): Promise<void> {
    await doDiscard(sessionId)
    // Update global state
    globalRecoverableSessions.value = globalRecoverableSessions.value.filter(
      (r) => r.session.id !== sessionId
    )
  }

  // Check on mount (only runs once due to singleton pattern)
  onMounted(checkOnMount)

  return {
    showRecoveryDialog,
    recoverableSessions: globalRecoverableSessions,
    isLoading: globalIsLoading,
    error: globalError,
    openRecoveryDialog,
    closeRecoveryDialog,
    discardSession,
  }
}

/**
 * Reset the singleton state (for testing purposes).
 */
export function resetRecoveryNotificationState(): void {
  hasChecked = false
  globalRecoverableSessions.value = []
  globalError.value = null
  globalIsLoading.value = false
}
